import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that waits for a boolean state to become false before executing a callback
 * This prevents UI flickering when multiple state changes need to happen sequentially
 */
const useWaitForClose = (isOpen: boolean) => {
    const pendingCallbackRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // When isOpen becomes false and we have a pending callback, execute it
        if (!isOpen && pendingCallbackRef.current) {
            const callback = pendingCallbackRef.current;
            pendingCallbackRef.current = null;
            callback();
        }
    }, [isOpen]);

    const executeAfterClose = useCallback(
        (callback: () => void) => {
            if (!isOpen) {
                // If already closed, execute immediately
                callback();
            } else {
                // Store the callback to execute once isOpen becomes false
                pendingCallbackRef.current = callback;
            }
        },
        [isOpen],
    );

    return executeAfterClose;
};

export default useWaitForClose;
